import { PkButton, PkAvatar, PkIcon } from '@uefadigital/panenka-react';
import clsx from 'clsx';
import type { ComponentProps } from 'react';
import { useMemo } from 'react';

import { useUser } from '../hooks/useUser';
import { useTranslateFn } from '../Translate';

import { AUTH_FLOW_TYPE, LoginSources } from '@shared-types/idp.types';
import IdpHandler from '@user/IdpHandler';
import { stripHtml } from '@utilities/dom';

import * as styles from './styles.module.scss';

export const LoginButton = ({
  hideIfLogged = true,
  type = AUTH_FLOW_TYPE.REGISTRATION,
  appearance = 'outlined',
  rounded,
  showLabel = true,
  showUsername,
  customUserPage,
  onClick,
}: {
  hideIfLogged?: boolean;
  type?: AUTH_FLOW_TYPE;
  appearance?: ComponentProps<typeof PkButton>['appearance'];
  rounded?: boolean;
  showLabel?: boolean;
  showUsername?: boolean;
  customUserPage?: string;
  onClick?: () => void;
}) => {
  const { logged, data: user } = useUser();
  const t = useTranslateFn();

  const isLogin = type === AUTH_FLOW_TYPE.LOGIN;

  const username = useMemo(
    () =>
      stripHtml(
        user?.profile?.nickname ?? user?.profile?.name ?? user?.profile?.firstName ?? user?.profile?.email ?? '',
      ),
    [user],
  );

  const handleClick = () => {
    isLogin ? IdpHandler.showLogin(LoginSources.MENU) : IdpHandler.showRegister(LoginSources.MENU);

    onClick?.();

    return false;
  };

  const handleProfileClick = () => {
    if (customUserPage) return;
    IdpHandler.showProfile();
  };

  return (
    <>
      {!logged && (
        <PkButton
          className={clsx('pk-d--flex pk-align-items--center', styles.loginBtn, rounded && styles.loginBtnRounded)}
          appearance={appearance}
          highContrast
          onPkClick={handleClick}
          width={isLogin ? 'auto' : '180'}
        >
          {showLabel && (
            <span className={clsx(isLogin && 'pk-font-size--s')}>{t(isLogin ? 'login' : 'join_for_free')}</span>
          )}
          {isLogin && <PkIcon className='pk-ml--xs' name='actions-profile' size={24} />}
        </PkButton>
      )}
      {logged && !hideIfLogged && (
        <PkButton
          className={clsx('pk-d--flex pk-align-items--center', rounded && styles.loginBtnRounded)}
          appearance={appearance}
          highContrast
          onPkClick={handleProfileClick}
          linkHref={customUserPage}
          width='auto'
        >
          {showUsername && !!username && (
            <span className={clsx('pk-font-size--s pk-line-height--md', styles.username)}>{username}</span>
          )}
          <PkAvatar
            className={clsx(styles.avatar, 'pk-ml--xs')}
            type='image'
            src={user?.profile?.thumbnailURL}
            fallbackImage='generic-head'
            size={24}
          />
        </PkButton>
      )}
    </>
  );
};
